import React from "react";
import Nav from "../components/Nav";
import Footer from "../components/Footer";
import SEO, { BreadcrumbSchema, ServiceSchema } from "../components/SEO";
import { Link } from "react-router-dom";
import { FadeUp } from "../components/Motion";
import { CONTACT, LICENSES, BRAND } from "../lib/content";

const pastPerformance = [
  { title: "Kissimmee River Restoration, C-38 Reach 3 Backfill", owner: "U.S. Army Corps of Engineers, Jacksonville District", loc: "Okeechobee County, FL", val: "$4.8M", slug: "kissimmee-river-restoration" },
  { title: "Hernando Beach Channel Dredging", owner: "Hernando County", loc: "Hernando Beach, FL", val: "$8.9M", slug: "hernando-beach-navigation-channel" },
  { title: "IR-2 Dredged Material Management Area", owner: "U.S. Army Corps of Engineers, Jacksonville District", loc: "Indian River County, FL", val: "$3.8M" },
  { title: "Island Creek Berm Repair, John H. Kerr Lake", owner: "U.S. Army Corps of Engineers, Wilmington District", loc: "Boydton, VA", val: "$2.0M" },
  { title: "Fisher House Safety Improvements", owner: "James A. Haley VA Medical Center", loc: "Tampa, FL", val: "$2.0M" },
  { title: "VA Front Lobby Renovation", owner: "James A. Haley VA Medical Center", loc: "Tampa, FL", slug: "tampa-va-front-lobby-renovation" },
  { title: "VA Medical Center Renovations", owner: "Department of Veterans Affairs", loc: "Tampa, Gainesville, Lake City and Bay Pines, FL", val: "25+ task orders" },
  { title: "Portugues Dam", owner: "U.S. Army Corps of Engineers (consulting)", loc: "Ponce, Puerto Rico", slug: "portugues-dam-consulting" },
  { title: "C-51 Improvements, Equitable Adjustment", owner: "Palm Beach County", loc: "Palm Beach County, FL", slug: "c-51-improvements-equitable-adjustment" },
];

const capabilities = [
  "Civil works, earthwork and dredged material management",
  "Dredging and navigation channels",
  "Healthcare renovation in occupied facilities",
  "Commercial and institutional construction",
  "Construction claims and requests for equitable adjustment",
  "FEMA Public Assistance and recovery program support",
];

const naics = [
  { code: "236220", t: "Commercial and Institutional Building Construction" },
  { code: "237990", t: "Other Heavy and Civil Engineering Construction" },
  { code: "541611", t: "Administrative Management and General Management Consulting" },
  { code: "561210", t: "Facilities Support Services" },
  { code: "562910", t: "Remediation Services" },
];

export default function Federal() {
  return (
    <>
      <SEO
        title="SDVOSB Federal Contractor in Florida"
        description="Service-Disabled Veteran-Owned general contractor with Corps of Engineers and VA past performance. Prime, subcontract and teaming."
        path="/federal"
      />
      <BreadcrumbSchema items={[{ name: "Home", url: "https://bcpeabody.com/" }, { name: "Federal Construction & Teaming", url: "https://bcpeabody.com/federal" }]} />
      <ServiceSchema serviceType="Federal construction and SDVOSB teaming" path="/federal" />
      <Nav transparentOnTop />

      <section className="relative min-h-[55vh] flex items-end overflow-hidden bg-ink text-cream-light">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-32 -right-32 w-[500px] h-[500px] rounded-full bg-copper/10 blur-[160px]" />
        </div>
        <img
          src="https://customer-assets-gfyr7b9c.emergentagent.net/job_company-site-new/artifacts/3bd2d6151a2fed5d_bcpeabody-kissimmee-river-restoration-reach-3-backfill-aerial-01.jpg"
          alt=""
          aria-hidden="true"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0" style={{ background: "linear-gradient(to right, rgba(46,41,35,0.92) 0%, rgba(46,41,35,0.78) 55%, rgba(46,41,35,0.55) 100%)" }} />
        <div className="relative max-w-[1200px] mx-auto px-6 md:px-10 w-full pb-14 pt-32">
          <div className="text-[11px] tracking-[0.18em] uppercase text-copper-light mb-5">Disaster Recovery</div>
          <h1 className="font-serif font-medium tracking-tight mb-5 leading-[1.05]" style={{ fontSize: "clamp(32px, 5vw, 60px)" }}>
            Federal Construction & Teaming
          </h1>
          <p className="text-cream-light/90 max-w-3xl" style={{ fontSize: 18 }}>
            Past federal construction from a Service-Disabled Veteran-Owned general contractor.
          </p>
        </div>
        <div className="absolute bottom-3 right-4 text-[10px] text-cream-light/70 tracking-wide">Photo: South Florida Water Management District</div>
      </section>

      <section className="bg-cream-light py-16 md:py-20">
        <div className="max-w-[1000px] mx-auto px-6 md:px-10">
          <FadeUp>
            <p className="text-ink/85 leading-relaxed" style={{ fontSize: 18 }}>
              BCPeabody has held more than 30 federal contract awards with the U.S. Army Corps of Engineers and the Department of Veterans Affairs, from dredging and earthwork to renovations inside occupied hospitals. We work as a prime contractor, subcontractor or teaming partner on construction, recovery and technical support.
            </p>
          </FadeUp>
        </div>
      </section>

      {/* Past performance */}
      <section data-testid="federal-past-performance" className="bg-cream py-16 md:py-20">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10">
          <h2 className="font-serif font-medium text-ink mb-10 tracking-tight" style={{ fontSize: "clamp(26px, 3.2vw, 36px)" }}>Past performance</h2>
          <div className="divide-y divide-ink/10 border-t border-ink/10">
            {pastPerformance.map((p) => (
              <article key={p.title} className="py-5 grid grid-cols-1 md:grid-cols-12 gap-4 items-baseline">
                <div className="md:col-span-6">
                  {p.slug ? (
                    <Link to={`/projects/${p.slug}`} className="font-serif text-xl text-ink tracking-tight hover:text-copper">
                      {p.title}
                    </Link>
                  ) : (
                    <div className="font-serif text-xl text-ink tracking-tight">{p.title}</div>
                  )}
                  <div className="text-sm text-ink/65 mt-1">{p.owner}</div>
                </div>
                <div className="md:col-span-4 text-sm text-ink/75">{p.loc}</div>
                <div className="md:col-span-2 text-sm text-copper md:text-right">{p.val || ""}</div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Capabilities */}
      <section className="bg-cream-light py-16 md:py-20">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10">
          <h2 className="font-serif font-medium text-ink mb-8 tracking-tight" style={{ fontSize: "clamp(26px, 3.2vw, 36px)" }}>Capabilities</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-3">
            {capabilities.map((c) => (
              <div key={c} className="flex items-start gap-3 py-2 border-b border-ink/10">
                <span className="w-4 h-px bg-copper mt-3 shrink-0" />
                <span className="text-ink/85">{c}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Company data */}
      <section data-testid="federal-company-data" className="bg-cream py-16 md:py-20">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10">
          <h2 className="font-serif font-medium text-ink mb-8 tracking-tight" style={{ fontSize: "clamp(26px, 3.2vw, 36px)" }}>Company data</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10 bg-cream-light p-8 md:p-10 border-t-2 border-copper">
            <div className="space-y-4 text-sm">
              <div><div className="text-[11px] tracking-[0.16em] uppercase text-copper mb-1">Legal name</div><div className="text-ink">{BRAND.legalName}</div></div>
              <div><div className="text-[11px] tracking-[0.16em] uppercase text-copper mb-1">Mailing address</div><div className="text-ink/85">{CONTACT.mailingAddress}</div></div>
              <div><div className="text-[11px] tracking-[0.16em] uppercase text-copper mb-1">Status</div><div className="text-ink">Service-Disabled Veteran-Owned Small Business</div></div>
              <div><div className="text-[11px] tracking-[0.16em] uppercase text-copper mb-1">Founded</div><div className="text-ink">2006, Tampa, Florida</div></div>
            </div>
            <div className="space-y-4 text-sm">
              <div>
                <div className="text-[11px] tracking-[0.16em] uppercase text-copper mb-1">Licenses</div>
                <div className="text-ink/85 space-y-1">
                  <div>{LICENSES.gc}</div>
                  <div>{LICENSES.plumb}</div>
                  <div>{LICENSES.pe}</div>
                  <div>{LICENSES.pairedLabel}</div>
                </div>
              </div>
              <div><div className="text-[11px] tracking-[0.16em] uppercase text-copper mb-1">Phone</div><a href={CONTACT.phoneHref} className="text-ink hover:text-copper">{CONTACT.phone}</a></div>
              <div><div className="text-[11px] tracking-[0.16em] uppercase text-copper mb-1">Email</div><a href={`mailto:${CONTACT.email}`} className="text-ink hover:text-copper break-all">{CONTACT.email}</a></div>
            </div>
          </div>

          <div className="mt-10">
            <h3 className="font-serif text-2xl text-ink mb-4 tracking-tight">NAICS</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {naics.map((n) => (
                <div key={n.code} className="flex items-baseline gap-4 border-l-2 border-copper pl-4 py-2">
                  <span className="font-serif text-lg text-ink">{n.code}</span>
                  <span className="text-ink/75 text-sm">{n.t}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-10 max-w-3xl text-ink/85 leading-relaxed">
            For teaming, subcontract or joint venture inquiries, contact Bob Carpenter, President & CEO, or Kimberly Carpenter at <a href={`mailto:${CONTACT.email}`} className="text-copper">{CONTACT.email}</a> or <a href={CONTACT.phoneHref} className="text-copper">{CONTACT.phone}</a>. We respond within one business day.
          </div>

          <div className="mt-8 flex flex-wrap gap-6">
            <Link to="/contact?type=federal" className="btn-copper">Request a Teaming Conversation →</Link>
            <Link to="/contact?type=federal" className="btn-dark">Request Our Capability Statement →</Link>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
}
